import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Pause, Play } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';

const G    = '#0F5A3A';
const GOLD = '#F5B327';

const SCHOOLS = [
  { name: 'Stanford',       full: 'Stanford University',       bg: '#8C1515', text: '#fff' },
  { name: 'Michigan',       full: 'Univ. of Michigan',         bg: '#00274C', text: '#FFCB05' },
  { name: 'Berkeley',       full: 'UC Berkeley',               bg: '#003262', text: '#FDB515' },
  { name: 'UT Austin',      full: 'Univ. of Texas',            bg: '#BF5700', text: '#fff' },
  { name: 'NYU',            full: 'New York University',       bg: '#57068C', text: '#fff' },
  { name: 'Northwestern',   full: 'Northwestern',              bg: '#4E2A84', text: '#fff' },
  { name: 'Howard',         full: 'Howard University',         bg: '#003A63', text: '#E8171C' },
  { name: 'Spelman',        full: 'Spelman College',           bg: '#003366', text: '#BF9B30' },
  { name: 'Georgia Tech',   full: 'Georgia Tech',              bg: '#B3A369', text: '#003057' },
  { name: 'Ohio State',     full: 'Ohio State',                bg: '#BB0000', text: '#fff' },
  { name: 'Boston Univ.',   full: 'Boston University',         bg: '#CC0000', text: '#fff' },
  { name: 'UCLA',           full: 'UCLA',                      bg: '#003B5C', text: '#FFD100' },
];

export const PartnerSchoolsSlider = () => {
  const isMobile = useIsMobile();
  const perPage = isMobile ? 2 : 6;

  const [groups, setGroups] = useState([]);
  const [current, setCurrent]   = useState(0);
  const [playing, setPlaying]   = useState(true);
  const [visible, setVisible]   = useState(true);
  const [hovering, setHovering] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    const g = [];
    for (let i = 0; i < SCHOOLS.length; i += perPage) {
      g.push(SCHOOLS.slice(i, i + perPage));
    }
    setGroups(g);
    setCurrent(0);
  }, [perPage]);

  const advance = useCallback(() => {
    setVisible(false);
    setTimeout(() => {
      setCurrent((p) => groups.length > 0 ? (p + 1) % groups.length : 0);
      setVisible(true);
    }, 420);
  }, [groups.length]);

  useEffect(() => {
    if (playing && !hovering && groups.length > 0) {
      intervalRef.current = setInterval(advance, 4200);
    }
    return () => clearInterval(intervalRef.current);
  }, [playing, hovering, advance, groups.length]);

  if (groups.length === 0) return null;

  return (
    <section
      style={{ background: '#fff', padding: isMobile ? '32px 0 28px' : '44px 0 40px', borderTop: '1px solid #EAE6DF' }}
      data-testid="partner-schools-section"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onFocus={() => setHovering(true)}
      onBlur={() => setHovering(false)}
    >
      <div style={{ maxWidth: '1080px', margin: '0 auto', padding: isMobile ? '0 20px' : '0 40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobile ? '20px' : '28px' }}>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '1.1rem' : 'clamp(1.15rem, 2vw, 1.45rem)', fontWeight: 900, color: G, letterSpacing: '-0.01em', margin: 0, lineHeight: 1.2 }}>
            Trusted by Top Colleges Nationwide
          </h2>
          <button type="button" aria-label={playing ? 'Pause carousel' : 'Play carousel'} onClick={() => setPlaying((p) => !p)} data-testid="carousel-play-pause"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '7px', border: '1.5px solid #E0DCD6', background: '#fff', cursor: 'pointer', color: G, flexShrink: 0, transition: 'background 0.15s' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#F4F0E6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}>
            {playing ? <Pause size={13} /> : <Play size={13} />}
          </button>
        </div>

        <div style={{ height: '1px', background: '#EAE6DF', marginBottom: isMobile ? '20px' : '28px' }} />

        <div style={{ minHeight: isMobile ? '80px' : '90px', position: 'relative' }}>
          <AnimatePresence mode="wait">
            {visible && (
              <motion.div key={current} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.42, ease: 'easeInOut' }}
                style={{ display: 'grid', gridTemplateColumns: `repeat(${perPage}, 1fr)`, gap: isMobile ? '10px' : '12px' }}>
                {groups[current] && groups[current].map((school) => (
                  <div key={school.name} style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '10px', padding: isMobile ? '10px' : '12px 14px', borderRadius: '10px', border: '1.5px solid #EAE6DF', background: '#FAFAF8' }}>
                    <div style={{ width: isMobile ? '36px' : '42px', height: isMobile ? '36px' : '42px', borderRadius: '8px', background: school.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: isMobile ? '10px' : '11px', fontWeight: 900, color: school.text, textTransform: 'uppercase', textAlign: 'center', lineHeight: 1.1 }}>{school.name.slice(0, 2)}</span>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{school.name}</div>
                      {!isMobile && <div style={{ fontSize: '10px', color: '#999', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{school.full}</div>}
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '7px', marginTop: isMobile ? '18px' : '24px' }}>
          {groups.map((_, i) => (
            <button key={i} type="button" aria-label={`Go to group ${i + 1}`} data-testid={`carousel-dot-${i}`}
              onClick={() => { setVisible(false); setTimeout(() => { setCurrent(i); setVisible(true); }, 420); }}
              style={{ width: i === current ? '20px' : '7px', height: '7px', borderRadius: '4px', background: i === current ? GOLD : '#D8D1C3', border: 'none', cursor: 'pointer', padding: 0, transition: 'width 0.3s, background 0.3s' }} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PartnerSchoolsSlider;
